'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Permit, PermitInspection, PermitStatus } from './types';

interface InspectionResultModalProps {
  permit: Permit | null;
  inspectionId: string | null;
  onClose: () => void;
  onRecorded: () => void;
}

const RESULT_OPTIONS: Array<{ key: PermitInspection['result']; label: string; color: string; bg: string; emoji: string }> = [
  { key: 'pass',             label: 'Pass',             color: '#16a34a', bg: '#dcfce7', emoji: '✅' },
  { key: 'conditional_pass', label: 'Conditional Pass', color: '#F5A623', bg: '#fef3c7', emoji: '⚠️' },
  { key: 'fail',             label: 'Fail',             color: '#dc2626', bg: '#fee2e2', emoji: '❌' },
];

const InspectionResultModal = ({ permit, inspectionId, onClose, onRecorded }: InspectionResultModalProps) => {
  const inspection = permit?.inspections?.find(i => i.id === inspectionId) ?? null;

  const [result, setResult] = useState<PermitInspection['result']>('pass');
  const [corrections, setCorrections] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!inspection) return;
    setResult('pass');
    setCorrections('');
    setNotes('');
    setSubmitting(false);
  }, [inspection?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!inspection) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [inspection, submitting, onClose]);

  if (!permit || !inspection) return null;

  const needsCorrections = result === 'fail' || result === 'conditional_pass';
  const isValid = !submitting && (!needsCorrections || corrections.trim().length >= 5);

  const handleSubmit = async () => {
    if (!isValid || !permit || !inspection) return;
    setSubmitting(true);

    const now = new Date().toISOString();
    const updatedInspections = (permit.inspections ?? []).map(ins => {
      if (ins.id !== inspection.id) return ins;
      return {
        ...ins,
        result,
        corrections: needsCorrections ? corrections.trim() : null,
        notes: notes.trim() || ins.notes || null,
        completed_at: now,
      };
    });

    // Determine permit status change
    let newPermitStatus: PermitStatus | null = null;
    if (result === 'pass') {
      if (inspection.type === 'final') newPermitStatus = 'passed';
      // mid-roof pass — stay in current status
    } else if (result === 'conditional_pass' || result === 'fail') {
      newPermitStatus = 'corrections_required';
    }

    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const existingTimeline = Array.isArray(permit.timeline_log) ? permit.timeline_log : [];
    const option = RESULT_OPTIONS.find(o => o.key === result);
    const newTimelineEntry = {
      action: `inspection_result_${result}`,
      actor: actorName,
      actor_id: currentUser?.id ?? null,
      timestamp: now,
      note: `${inspection.type} inspection by ${inspection.inspector ?? 'inspector'}: ${option?.label ?? result}${needsCorrections ? ` — corrections: ${corrections.trim().slice(0, 120)}` : ''}`,
    };

    const updatePayload: Record<string, unknown> = {
      inspections: updatedInspections,
      timeline_log: [...existingTimeline, newTimelineEntry],
      updated_at: now,
    };
    if (newPermitStatus) updatePayload.status = newPermitStatus;

    try {
      const { error } = await supabase.from('permits').update(updatePayload).eq('id', permit.id);
      if (error) throw error;
      toast.success(`${option?.emoji} ${option?.label} recorded for ${inspection.type} inspection`, { duration: 4000 });
      onRecorded();
      setSubmitting(false);
      onClose();
    } catch (err) {
      console.error('Failed to record inspection result:', err);
      toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown'}`, { duration: 6000 });
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="result-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100">
          <h3 id="result-modal-title" className="text-lg font-bold text-[#1C2E5E]">Record Inspection Result</h3>
          <p className="text-[12px] text-gray-500 mt-0.5 font-mono">
            {permit.job_id} · <span className="capitalize">{inspection.type}</span> · {new Date(inspection.scheduled_at).toLocaleDateString()}
          </p>
          {inspection.inspector && (
            <p className="text-[11px] text-gray-500 mt-0.5">Inspector: {inspection.inspector}</p>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Result */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Result <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {RESULT_OPTIONS.map(opt => {
                const active = result === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setResult(opt.key)}
                    className="px-2 py-3 rounded-lg text-[11px] font-bold transition-all"
                    style={{
                      background: active ? opt.color : opt.bg,
                      color: active ? '#fff' : opt.color,
                      boxShadow: active ? `0 4px 12px ${opt.color}44` : 'none',
                    }}
                  >
                    <div className="text-lg mb-0.5">{opt.emoji}</div>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Corrections (conditional) */}
          {needsCorrections && (
            <div>
              <label htmlFor="res-corrections" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                Corrections Required <span className="text-red-500">*</span>
              </label>
              <textarea
                id="res-corrections"
                value={corrections}
                onChange={e => setCorrections(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Describe exactly what needs to be fixed before re-inspection."
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-red-200 bg-red-50/30 focus:bg-white focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 transition-all resize-none"
              />
              <p className="text-[11px] text-gray-400 mt-1">{corrections.length}/1000 (min 5)</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label htmlFor="res-notes" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Inspector Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="res-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Any observations or details to keep on record."
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{
              background: RESULT_OPTIONS.find(o => o.key === result)?.color ?? '#1C2E5E',
            }}
          >
            {submitting ? 'Recording…' : 'Record Result'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InspectionResultModal;
